<template>
  <div class="location-search-section">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-md font-semibold text-gray-700">Location Search</h3>
    </div>
    
    <div class="relative">
      <input
        ref="locationSearchInput"
        type="text"
        v-model="searchQuery"
        @keydown.enter="handleSearchEnter"
        @keydown.down="handleArrowDown"
        @keydown.up="handleArrowUp"
        @keydown.escape="clearSearch"
        @input="onSearchInput"
        placeholder="Search for a location..."
        class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
      />
      <button
        @click="searchLocation"
        :disabled="!searchQuery.trim() || searching"
        class="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg v-if="!searching" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
        <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </button>
    </div>
    
    <!-- Search Results Dropdown -->
    <div v-if="searchResults.length > 0" class="mt-2 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
      <button
        v-for="(result, index) in searchResults"
        :key="index"
        :ref="`searchResult${index}`"
        @click="selectResult(result)"
        @keydown.enter="selectResult(result)"
        :class="[
          'w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm transition-colors',
          selectedIndex === index ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
        ]"
      >
        <div class="flex items-center justify-between">
          <div class="font-medium text-gray-800">{{ result.name }}</div>
          <div v-if="result.is_us_location" class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
            USA
          </div>
          <div v-else-if="result.country" class="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
            {{ result.country }}
          </div>
        </div>
        <div class="text-xs text-gray-500 mt-1">
          <div v-if="result.context_info" class="font-medium text-gray-600">{{ result.context_info }}</div>
          <div class="truncate">{{ result.formatted_address }}</div>
        </div>
      </button>
    </div>
    
    <!-- No Results Message -->
    <div v-if="showNoResults" class="mt-2 text-sm text-gray-500 italic">
      No locations found. Try a different search term.
    </div>
  </div>
</template>

<script>
export default {
  name: 'LocationSearch',
  
  props: {
    // No props needed - component is self-contained
  },
  
  data() {
    return {
      searchQuery: '',
      searchResults: [],
      searching: false,
      showNoResults: false,
      selectedIndex: -1,
      searchTimeout: null,
      placesService: null,
      tempMapDiv: null
    }
  },
  
  mounted() {
    // Initialize Google Places service
    this.initializePlacesService();
    
    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeDestroy() {
    // Clean up
    this.clearSearchTimeout();
    document.removeEventListener('click', this.handleClickOutside);
    this.cleanupPlacesService();
  },
  
  beforeUnmount() {
    // Clean up
    this.clearSearchTimeout();
    document.removeEventListener('click', this.handleClickOutside);
    this.cleanupPlacesService();
  },
  
  methods: {
    initializePlacesService() {
      // Wait for Google Maps API to be available
      const initService = () => {
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          // Create a hidden div for the PlacesService (required by Google Maps API)
          this.tempMapDiv = document.createElement('div');
          this.tempMapDiv.style.display = 'none';
          this.tempMapDiv.setAttribute('data-location-search-temp', 'true');
          document.body.appendChild(this.tempMapDiv);
          
          // Create a temporary map for the PlacesService
          const tempMap = new google.maps.Map(this.tempMapDiv, {
            center: { lat: 39.8283, lng: -98.5795 },
            zoom: 4
          });
          
          // Initialize PlacesService
          this.placesService = new google.maps.places.PlacesService(tempMap);
        } else {
          // Retry after 100ms if Google Maps isn't ready yet
          setTimeout(initService, 100);
        }
      };
      
      initService();
    },

    onSearchInput() {
      // Clear previous timeout
      this.clearSearchTimeout();
      
      // Reset selection
      this.selectedIndex = -1;
      
      // Auto-search after 500ms of no typing
      if (this.searchQuery.trim().length >= 3) {
        this.searchTimeout = setTimeout(() => {
          this.searchLocation();
        }, 500);
      } else {
        // Clear results if query is too short
        this.searchResults = [];
        this.showNoResults = false;
      }
    },
    
    searchLocation() {
      if (!this.searchQuery.trim() || this.searching || !this.placesService) {
        return;
      }

      this.searching = true;
      this.showNoResults = false;
      this.searchResults = [];

      // Use Google Places Text Search with bias towards US locations
      const request = {
        query: this.searchQuery.trim(),
        fields: ['place_id', 'name', 'formatted_address', 'geometry', 'types', 'address_components'],
        // Bias results towards the United States
        locationBias: {
          radius: 2000000, // 2000km radius
          center: { lat: 39.8283, lng: -98.5795 } // Center of USA
        }
      };

      this.placesService.textSearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
          // Process and enhance results with better location context
          const processedResults = this.processSearchResults(results);
          
          // Check if we have good US results
          const usResults = processedResults.filter(r => r.is_us_location);
          
          // If we don't have many US results, try a more specific US search
          if (usResults.length < 2 && !this.searchQuery.includes('USA') && !this.searchQuery.includes('United States')) {
            this.performUSSpecificSearch(processedResults);
            return;
          }
          
          // Limit to top 5 results for better UX
          this.searchResults = processedResults.slice(0, 5);
          this.showNoResults = false;
          this.selectedIndex = -1; // Reset selection
        } else {
          this.searchResults = [];
          this.selectedIndex = -1;
          
          // Only show "no results" if user explicitly searched (not auto-search)
          if (this.searchQuery.trim().length >= 3) {
            this.showNoResults = true;
            
            // Hide no results message after 3 seconds
            setTimeout(() => {
              this.showNoResults = false;
            }, 3000);
          }
        }
        
        this.searching = false;
      });
    },

    processSearchResults(results) {
      return results.map(place => {
        // Determine if this is a US location
        const isUSLocation = this.isUSLocation(place);
        
        // Extract better context information
        const contextInfo = this.extractLocationContext(place);
        
        // Get country information
        const country = this.extractCountry(place);
        
        return {
          place_id: place.place_id,
          name: place.name,
          formatted_address: place.formatted_address,
          geometry: place.geometry,
          types: place.types || [],
          is_us_location: isUSLocation,
          context_info: contextInfo,
          country: country,
          // Calculate relevance score for sorting
          relevance_score: this.calculateRelevanceScore(place, this.searchQuery.trim())
        };
      }).sort((a, b) => {
        // Sort by US locations first, then by relevance score
        if (a.is_us_location && !b.is_us_location) return -1;
        if (!a.is_us_location && b.is_us_location) return 1;
        return b.relevance_score - a.relevance_score;
      });
    },

    isUSLocation(place) {
      // Check address components for country
      if (place.address_components) {
        const countryComponent = place.address_components.find(
          component => component.types.includes('country')
        );
        if (countryComponent) {
          return countryComponent.short_name === 'US' || countryComponent.long_name === 'United States';
        }
      }
      
      // Fallback: check if formatted address contains US indicators
      const address = place.formatted_address.toLowerCase();
      return address.includes(', usa') || 
             address.includes(', united states') ||
             address.includes(', us ') ||
             address.endsWith(', us');
    },

    extractLocationContext(place) {
      if (!place.address_components) return null;
      
      // Try to build a meaningful context string
      const state = place.address_components.find(c => c.types.includes('administrative_area_level_1'));
      const county = place.address_components.find(c => c.types.includes('administrative_area_level_2'));
      const city = place.address_components.find(c => c.types.includes('locality'));
      
      if (state && city) {
        return `${city.long_name}, ${state.short_name}`;
      } else if (state && county) {
        return `${county.long_name}, ${state.short_name}`;
      } else if (state) {
        return state.long_name;
      }
      
      return null;
    },

    extractCountry(place) {
      if (!place.address_components) return null;
      
      const countryComponent = place.address_components.find(
        component => component.types.includes('country')
      );
      
      return countryComponent ? countryComponent.short_name : null;
    },

    calculateRelevanceScore(place, query) {
      let score = 0;
      const queryLower = query.toLowerCase();
      const nameLower = place.name.toLowerCase();
      const addressLower = place.formatted_address.toLowerCase();
      
      // Exact name match gets highest score
      if (nameLower === queryLower) score += 100;
      else if (nameLower.includes(queryLower)) score += 50;
      
      // Address match
      if (addressLower.includes(queryLower)) score += 25;
      
      // Prefer certain types of places
      if (place.types) {
        if (place.types.includes('locality')) score += 20; // Cities
        if (place.types.includes('administrative_area_level_1')) score += 15; // States
        if (place.types.includes('administrative_area_level_2')) score += 10; // Counties
        if (place.types.includes('postal_code')) score += 5; // ZIP codes
      }
      
      return score;
    },

    performUSSpecificSearch(existingResults = []) {
      // Try searching with "USA" appended to get more US-specific results
      const usQuery = `${this.searchQuery.trim()}, USA`;
      
      const usRequest = {
        query: usQuery,
        fields: ['place_id', 'name', 'formatted_address', 'geometry', 'types', 'address_components']
      };

      this.placesService.textSearch(usRequest, (usResults, usStatus) => {
        this.searching = false;
        
        if (usStatus === google.maps.places.PlacesServiceStatus.OK && usResults && usResults.length > 0) {
          // Process US-specific results
          const processedUSResults = this.processSearchResults(usResults);
          
          // Combine with existing results, prioritizing US results
          const combinedResults = [...processedUSResults, ...existingResults];
          
          // Remove duplicates based on place_id
          const uniqueResults = combinedResults.filter((result, index, self) => 
            index === self.findIndex(r => r.place_id === result.place_id)
          );
          
          this.searchResults = uniqueResults.slice(0, 5);
          this.showNoResults = false;
          this.selectedIndex = -1;
        } else {
          // Fall back to original results if US search fails
          this.searchResults = existingResults.slice(0, 5);
          this.selectedIndex = -1;
          
          if (this.searchQuery.trim().length >= 3) {
            this.showNoResults = true;
            setTimeout(() => {
              this.showNoResults = false;
            }, 3000);
          }
        }
      });
    },
    
    selectResult(result) {
      if (!result.geometry || !result.geometry.location) {
        console.error('Invalid location result:', result);
        return;
      }

      // Clear search results and reset selection
      this.searchResults = [];
      this.showNoResults = false;
      this.selectedIndex = -1;

      // Get the location coordinates
      const location = result.geometry.location;
      const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
      const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

      // Clear the search input
      this.searchQuery = '';

      // Emit the location selection event to parent
      this.$emit('location-selected', {
        coordinates: { lat, lng },
        name: result.name,
        address: result.formatted_address
      });
    },
    
    handleSearchEnter() {
      if (this.selectedIndex >= 0 && this.searchResults[this.selectedIndex]) {
        // Select the highlighted result
        this.selectResult(this.searchResults[this.selectedIndex]);
      } else {
        // Perform search
        this.searchLocation();
      }
    },

    handleArrowDown(event) {
      event.preventDefault();
      if (this.searchResults.length > 0) {
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.searchResults.length - 1);
        this.scrollToSelectedResult();
      }
    },

    handleArrowUp(event) {
      event.preventDefault();
      if (this.searchResults.length > 0) {
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.scrollToSelectedResult();
      }
    },

    scrollToSelectedResult() {
      if (this.selectedIndex >= 0) {
        this.$nextTick(() => {
          const resultElement = this.$refs[`searchResult${this.selectedIndex}`];
          if (resultElement && resultElement[0]) {
            resultElement[0].scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          }
        });
      }
    },
    
    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
      this.showNoResults = false;
      this.selectedIndex = -1;
      this.clearSearchTimeout();
    },
    
    clearSearchTimeout() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
    },
    
    handleClickOutside(event) {
      // Check if click is outside the location search section
      const searchSection = event.target.closest('.location-search-section');
      if (!searchSection && this.searchResults.length > 0) {
        this.searchResults = [];
        this.showNoResults = false;
        this.selectedIndex = -1;
      }
    },

    cleanupPlacesService() {
      // Clean up the temporary map div if it exists
      if (this.tempMapDiv && document.body.contains(this.tempMapDiv)) {
        document.body.removeChild(this.tempMapDiv);
        this.tempMapDiv = null;
      }
    }
  }
}
</script>

<style scoped>
/* Location search styling */
.location-search-section .relative {
  position: relative;
}

.location-search-section input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.location-search-section button:hover:not(:disabled) {
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 4px;
}

/* Search results dropdown styling */
.location-search-section .bg-white {
  position: relative;
  z-index: 50;
}

.location-search-section button:focus {
  outline: 2px solid #3b82f6;
  outline-offset: -2px;
}
</style>